import * as FSR from "src/generated/hooks/form-service/requests";
import { triggerDownload } from "src/services/dataTransfer";
import { z } from "zod";
import {
  RuleImportExport,
  RuleImportExportSchema,
  RuleSchema,
} from "./ruleSchema";

export type RuleImportError = { heading: string; messages: string[] };

const RULE_TYPE_LABEL: Record<FSR.RuleErrorGroup["ruleType"], string> = {
  ConnectForms: "Connect Forms",
  AutoRevoke: "Auto Revoke",
  ClearLowerRanked: "Clear Lower Ranked",
  AutoDecision: "Auto Decision",
  DynamicSibling: "Dynamic Sibling",
  FormQuestion: "Form Question",
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const ruleNameAt = (
  rules: readonly unknown[],
  index: number
): string | undefined => {
  const name = asRecord(rules[index]).name;
  return typeof name === "string" && name.length > 0 ? name : undefined;
};

/**
 * `[Failed] Rule <name>, Rule <key> - <Type>`, gracefully dropping the pieces
 * that are missing (e.g. a rule with no name won't render a stray comma).
 */
const formatHeading = (opts: {
  ruleType?: string;
  name?: string;
  key?: string | null;
}): string => {
  if (opts.ruleType === "ConnectForms") {
    return "[Failed] Connect Forms Rule";
  }
  const label =
    opts.ruleType && opts.ruleType in RULE_TYPE_LABEL
      ? RULE_TYPE_LABEL[opts.ruleType as FSR.RuleErrorGroup["ruleType"]]
      : "Rule";
  const parts: string[] = [];
  if (opts.name) parts.push(`Rule ${opts.name}`);
  if (opts.key) parts.push(`Rule ${opts.key}`);
  return parts.length > 0
    ? `[Failed] ${parts.join(", ")} - ${label}`
    : `[Failed] ${label}`;
};

const headingForParsedRule = (rule: unknown): string => {
  const record = asRecord(rule);
  return formatHeading({
    ruleType:
      typeof record.rule_type === "string" ? record.rule_type : undefined,
    name: typeof record.name === "string" ? record.name : undefined,
    key: typeof record.key === "string" ? record.key : undefined,
  });
};

/**
 * Turns client-side zod validation issues into the same grouped errors as the
 * backend, grouped by the rule index in the issue path.
 */
export const zodIssuesToRuleImportErrors = (
  issues: readonly z.ZodIssue[],
  rules: readonly unknown[] = []
): RuleImportError[] => {
  const messagesByIndex = new Map<number, string[]>();
  const order: number[] = [];
  for (const issue of issues) {
    const first = issue.path[0];
    const index = typeof first === "number" ? first : -1;
    if (!messagesByIndex.has(index)) {
      messagesByIndex.set(index, []);
      order.push(index);
    }
    messagesByIndex.get(index)?.push(issue.message);
  }
  return order.map((index) => {
    const messages = Array.from(new Set(messagesByIndex.get(index) ?? []));
    return index < 0
      ? { heading: "Invalid rule configuration", messages }
      : { heading: headingForParsedRule(rules[index]), messages };
  });
};

/**
 * Turns a single rule's zod validation issues into one headed, bulleted error.
 * Used when each rule in the array is parsed independently so that a structural
 * failure in one rule does not discard the rules that parsed successfully.
 */
export const ruleParseError = (
  rule: unknown,
  issues: readonly z.ZodIssue[]
): RuleImportError => ({
  heading: headingForParsedRule(rule),
  messages: Array.from(new Set(issues.map((issue) => issue.message))),
});

/**
 * Turns the backend's `errorGroups` into headed, bulleted errors. The heading
 * needs the rule's name, which the groups don't carry, so it is resolved by
 * `index` from the rules that were submitted (import) or exported.
 */
export const buildRuleImportErrors = (
  errorGroups: readonly FSR.RuleErrorGroup[],
  rules: readonly unknown[] = []
): RuleImportError[] =>
  errorGroups.map((group) => ({
    heading: formatHeading({
      ruleType: group.ruleType,
      name: ruleNameAt(rules, group.index),
      key: group.ruleKey,
    }),
    messages: group.errors.map((error) => error.error),
  }));

export type RulePartition = {
  /**
   * The rules to send to the backend. Only meaningful when `crossRuleErrors` is
   * empty; when a cross-rule invariant fails there is no safe subset to send.
   */
  validRules: RuleImportExport;
  /** One entry per rule that failed structural (per-rule) validation. */
  parseErrors: RuleImportError[];
  /**
   * Cross-rule invariant failures (duplicate keys, more than one ConnectForms)
   * among the structurally valid rules. Non-empty means do not send anything.
   */
  crossRuleErrors: RuleImportError[];
};

/**
 * Parses an array of rules one at a time so a structural failure in one rule
 * does not discard the rules that parse successfully. The survivors are then
 * checked against the cross-rule invariants (unique keys, single ConnectForms).
 * Callers send `validRules` only when `crossRuleErrors` is empty, and always
 * surface `parseErrors`.
 */
export const partitionRulesForImport = (
  rules: readonly unknown[]
): RulePartition => {
  const survivors: unknown[] = [];
  const parseErrors: RuleImportError[] = [];
  rules.forEach((rawRule) => {
    const result = RuleSchema.safeParse(rawRule);
    if (result.success) {
      survivors.push(result.data);
    } else {
      parseErrors.push(ruleParseError(rawRule, result.error.issues));
    }
  });

  const arrayValidation = RuleImportExportSchema.safeParse(survivors);
  if (!arrayValidation.success) {
    return {
      validRules: [],
      parseErrors,
      crossRuleErrors: zodIssuesToRuleImportErrors(
        arrayValidation.error.issues,
        survivors
      ),
    };
  }

  return { validRules: arrayValidation.data, parseErrors, crossRuleErrors: [] };
};

/**
 * Rules copied from an existing form template are always created inactive, so an
 * admin can review them before turning them on (PR-11). The legacy ConnectForms
 * rule has no active flag and is returned unchanged.
 */
export const deactivateRules = (rules: RuleImportExport): RuleImportExport =>
  rules.map((rule) =>
    rule.rule_type === "ConnectForms" ? rule : { ...rule, active: false }
  );

export const downloadRuleConfiguration = (rules: unknown): void => {
  void triggerDownload(
    new Blob([JSON.stringify(rules, undefined, 2)], {
      type: "application/json",
    }),
    "ruleConfiguration.json"
  );
};
